import styles from './ccf-pop-over.module.scss';
import React from 'react';
import {
  Popover,
  Typography,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';

import { CcfOverflowIcon } from '../../icons/ccf-overflow-icon/ccf-overflow-icon';
import { IndicatorActionType } from '@nice-ccf/common-sdk';
import CcfTooltip from '../ccf-tooltip/ccf-tooltip';
import {
  CcfTranslationKey,
  useTranslator,
} from '../../ccf-translator/ccf-translator';
import { useTheme } from '@mui/material';
import ccfPopOverStyles from './ccf-pop-over.style';
import CcfDivider, {
  DividerOrientation,
  DividerVariant,
} from '../ccf-divider/ccf-divider';

export interface PopOverData {
  popOverHeader?: string;
  menuItems: Array<PopOverMenuItems>;
}

export interface PopOverMenuItems {
  items: Array<PopOverMenuItem>;
}

export interface PopOverMenuItem {
  label?: string;
  translationKey?: CcfTranslationKey;
  icon?: React.ReactElement | string;
  type?: string | IndicatorActionType;
  closeOnSelection?: boolean;
  toolTip?: string;
  url?: string;
  disabled?: boolean;
  id?: string;
  dividerEnable?: boolean;
}

export type ToolTipPlacement =
  | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top';

export interface CcfPopOverProps {
  optionList: PopOverData;
  onPopOverItemSelection(
    item: PopOverMenuItem,
    e: React.MouseEvent<HTMLElement>
  ): React.MouseEventHandler;
  navigateToInitialPopOverState?: React.MouseEventHandler;
  endIconComponent?: React.ReactNode;
  PaperProps?: {
    style?: { [key: string]: string };
  };
  iconComponent?: React.ReactNode;
  labelComponent?: React.ReactNode;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
  };
  style?: {
    [key: string]: string;
  };
  tooltip?: boolean;
  setShowTooltip?: React.Dispatch<React.SetStateAction<boolean>>;
  customId?: string;
  tooltipPlacement?: ToolTipPlacement;
  popOverActionIconWrapper?: any;
  popOverMain?: any;
  popOverActionLabelStyle?: any;
  buttonStyle?: any;
}

/**
 * component to display popover
 * @param param0 - CcfPopOverProps
 * @example <CcfPopOver />
 */
export function CcfPopOver({
  optionList,
  onPopOverItemSelection,
  navigateToInitialPopOverState,
  iconComponent = <CcfOverflowIcon />,
  endIconComponent,
  labelComponent,
  anchorOrigin = { vertical: 'center', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  style,
  popOverActionIconWrapper,
  popOverMain,
  tooltip,
  setShowTooltip,
  customId,
  popOverActionLabelStyle,
  buttonStyle = {},
  tooltipPlacement = 'right-end',
  ...rest
}: CcfPopOverProps) {
  const theme = useTheme();
  const popOverStyle = ccfPopOverStyles(theme);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [translate] = useTranslator();

  /**
   * Function to open pop over menu
   * @param event - any
   * @example openPopOverMenu(event)
   */
  const openPopOverMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
    navigateToInitialPopOverState && navigateToInitialPopOverState(event);
  };

  /**
   * Function to close popover
   * @example onClose()
   */
  const onClose = () => {
    setAnchorEl(null);
  };

  /**
   * Function to close popover
   * @example setTooltipFlag()
   */
  const setTooltipFlag = () => {
    setShowTooltip && setShowTooltip(false);
  };

  /**
   *
   * @param item - any
   * @param e - React.MouseEvent
   * @example onItemSelection(item,e)
   */
  function onItemSelection(item: any, e: React.MouseEvent<HTMLElement>) {
    onPopOverItemSelection(item, e)(item);
    item.closeOnSelection && onClose();
  }

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        data-testid="ccf-popover-button"
        aria-controls="callControlDialog"
        aria-haspopup="true"
        onClick={openPopOverMenu}
        className="popOverOverflow"
        disableRipple
        sx={{
          gap: '7px',
          padding: 0,
          width: labelComponent ? '100%' : '24px',
          height: '40px',
          minWidth: 'auto',
          '&.MuiButtonBase-root:hover': { bgcolor: 'transparent' },
          ...popOverStyle.popOverOverflow,
          ...buttonStyle,
        }}
        id="agent_activeConatct_popOver"
      >
        {iconComponent}
        {labelComponent}
        {endIconComponent}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        style={style}
        onMouseEnter={setTooltipFlag}
        PaperProps={{
          onMouseLeave: handlePopoverClose,
        }}
        {...rest}
      >
        {
          <Typography id={customId} className={styles['ccfPopOver']}>
            {optionList.popOverHeader && (
              <div className="flexDisplay header">
                <div className="flexDisplay">
                  <span className="headerTitle">
                    {optionList.popOverHeader}
                  </span>
                </div>
              </div>
            )}
            {optionList.menuItems.map(
              (parentItem: PopOverMenuItems, index: number) => (
                <MenuList
                  sx={popOverMain ? popOverMain : popOverStyle.popOverMain}
                  key={index}
                >
                  {parentItem.items?.map(
                    (childItem: PopOverMenuItem, index: number) => (
                      <MenuItem
                        id={`agent_activeContact_inboxPopOver_${childItem.label}`}
                        key={index}
                        className="flexDisplay"
                        sx={popOverStyle.popOverOptions}
                        onClick={(e) => onItemSelection(childItem, e)}
                        disabled={childItem.disabled}
                        data-testId={`popover-menu-item-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexDirection: 'column',
                          padding: '0px',
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 10px 5px 10px',
                          }}
                        >
                          {childItem.icon && (
                            <ListItemIcon
                              className={
                                popOverActionIconWrapper
                                  ? popOverActionIconWrapper
                                  : 'popOverActionIconWrapper'
                              }
                            >
                              {childItem.icon}
                            </ListItemIcon>
                          )}
                          {tooltip ? (
                            <CcfTooltip
                              title={
                                typeof childItem.toolTip === 'string'
                                  ? childItem.toolTip
                                  : ''
                              }
                              placement={tooltipPlacement}
                            >
                              <ListItemText
                                className={'popOverActionLabelWrapper'}
                              >
                                <span className="popOverActionLabel">
                                  {childItem.translationKey
                                    ? translate(childItem.translationKey)
                                    : childItem.label}
                                </span>
                              </ListItemText>
                            </CcfTooltip>
                          ) : (
                            <ListItemText className="popOverActionLabelWrapper">
                              <span
                                className={
                                  popOverActionLabelStyle
                                    ? popOverActionLabelStyle
                                    : 'popOverActionLabel'
                                }
                              >
                                {childItem.translationKey
                                  ? translate(childItem.translationKey)
                                  : childItem.label}
                              </span>
                            </ListItemText>
                          )}
                        </div>
                        {childItem?.dividerEnable && (
                          <CcfDivider
                            orientation={DividerOrientation.HORIZONTAL}
                            variant={DividerVariant.FULLWIDTH}
                            sx={{ width: '100%', borderColor: '#D2D8DB' }}
                            // className={styles.dividerStyles}
                          />
                        )}
                      </MenuItem>
                    )
                  )}
                </MenuList>
              )
            )}
          </Typography>
        }
      </Popover>
    </>
  );
}
export default CcfPopOver;
